import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  ClaudeFlowSession,
  CreateSessionRequest,
  UpdateSessionRequest,
  SessionLogs,
  SessionTemplate,
  CreateSessionTemplateRequest,
} from './types';
import { ClaudeFlowApi } from './ClaudeFlowApi';

export class ClaudeFlowClient implements ClaudeFlowApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(
    discoveryApi: DiscoveryApi,
    fetchApi: FetchApi,
  ) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('claude-flow');
  }

  private async fetch<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}${path}`;

    const response = await this.fetchApi.fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Claude Flow API error: ${response.status} - ${error}`);
    }

    // Handle 204 No Content responses or empty responses
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    // Fallback for non-JSON responses
    return response.text() as T;
  }

  async listSessions(): Promise<ClaudeFlowSession[]> {
    return this.fetch<ClaudeFlowSession[]>('/sessions');
  }

  async getSession(sessionId: string): Promise<ClaudeFlowSession> {
    return this.fetch<ClaudeFlowSession>(`/sessions/${sessionId}`);
  }

  async createSession(request: CreateSessionRequest): Promise<ClaudeFlowSession> {
    return this.fetch<ClaudeFlowSession>('/sessions', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async updateSession(
    sessionId: string,
    request: UpdateSessionRequest,
  ): Promise<ClaudeFlowSession> {
    return this.fetch<ClaudeFlowSession>(`/sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  async deleteSession(sessionId: string): Promise<void> {
    await this.fetch<void>(`/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async startSession(sessionId: string): Promise<ClaudeFlowSession> {
    return this.fetch<ClaudeFlowSession>(`/sessions/${sessionId}/start`, {
      method: 'POST',
    });
  }

  async stopSession(sessionId: string): Promise<ClaudeFlowSession> {
    return this.fetch<ClaudeFlowSession>(`/sessions/${sessionId}/stop`, {
      method: 'POST',
    });
  }

  async getSessionLogs(sessionId: string): Promise<SessionLogs> {
    return this.fetch<SessionLogs>(`/sessions/${sessionId}/logs`);
  }

  async getRepositories(): Promise<string[]> {
    return this.fetch<string[]>('/repositories');
  }

  async getBranches(repository: string): Promise<string[]> {
    return this.fetch<string[]>(`/repositories/${encodeURIComponent(repository)}/branches`);
  }

  async getStartupScripts(): Promise<{ value: string; label: string }[]> {
    return this.fetch<{ value: string; label: string }[]>('/startup-scripts');
  }

  async getFuelixTokens(): Promise<{ tokens: string[]; hasTokenSecret: boolean }> {
    try {
      return await this.fetch<{ tokens: string[]; hasTokenSecret: boolean }>('/fuelix-tokens');
    } catch (error) {
      // If we get a 404, it means no tokens found, return appropriate response
      if (error instanceof Error && error.message.includes('404')) {
        return { tokens: [], hasTokenSecret: false };
      }
      throw error;
    }
  }

  async listSessionTemplates(): Promise<SessionTemplate[]> {
    return this.fetch<SessionTemplate[]>('/templates');
  }

  async getSessionTemplate(templateId: string): Promise<SessionTemplate> {
    return this.fetch<SessionTemplate>(`/templates/${templateId}`);
  }

  async createSessionTemplate(request: CreateSessionTemplateRequest): Promise<SessionTemplate> {
    return this.fetch<SessionTemplate>('/templates', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async deleteSessionTemplate(templateId: string): Promise<void> {
    await this.fetch<void>(`/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  async getSessionStats(sessionId: string): Promise<{
    sessionId: string;
    containerId?: string;
    stats?: any;
    info?: any;
  }> {
    return this.fetch(`/sessions/${sessionId}/stats`);
  }

  async getSessionState(sessionId: string): Promise<{
    sessionId: string;
    containerId?: string;
    state: string;
    sessionStatus: string;
  }> {
    return this.fetch(`/sessions/${sessionId}/state`);
  }

  getSessionStatsStreamUrl(sessionId: string): string {
    // Note: We'll need to get the actual backend URL for SSE
    // For now, we'll construct it based on the window location
    const protocol = window.location.protocol;
    const host = window.location.hostname;
    const port = window.location.port === '3000' ? '7007' : window.location.port;
    return `${protocol}//${host}:${port}/api/claude-flow/sessions/${sessionId}/stats/stream`;
  }

  async getAvailableUsers(): Promise<Array<{
    ref: string;
    name: string;
    displayName?: string;
    email?: string;
  }>> {
    return this.fetch('/auth/users');
  }

  async getAvailableGroups(): Promise<Array<{
    ref: string;
    name: string;
    displayName?: string;
    members?: string[];
  }>> {
    return this.fetch('/auth/groups');
  }

  async getUserGroups(userRef: string): Promise<string[]> {
    return this.fetch(`/auth/user/${encodeURIComponent(userRef)}/groups`);
  }

  async getAuthenticatedContainerUrl(sessionId: string): Promise<string> {
    const response = await this.fetch<{ url: string }>(`/sessions/${sessionId}/auth-url`);
    return response.url;
  }

  async getConfig(): Promise<{
    backstageUrl: string;
    requireAuth: boolean;
  }> {
    return this.fetch('/config');
  }
}
